<template>
    <div v-if="topic?.uuid" class="app-project-sources-golden-source-content">
      <div class="app-project-sources-golden-source-content__header">
        <div class="app-project-sources-golden-source-content__title">
          <span v-if="topic.reviewed" class="element-featured-icon is--success is--sm">
              <svg class="element-icon--check-circle element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-check-circle"></use></svg>
            </span>
            <span v-if="topic.flagged || isTopicVirtuallyFlagged" class="element-featured-icon is--warning is--sm" :title="flagStatusTitle">
              <svg class="element-icon--eye-closed element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-eye-closed"></use></svg>
            </span>
            <span v-else-if="isTopicPartiallyFlagged" 
              class="app-project-sources-golden-source-navbar-partial-flag"
              :title="flagStatusTitle">
              <span class="element-featured-icon is--warning is--sm">
                <svg class="element-icon--eye-closed element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-eye-closed"></use></svg>
              </span>
              <span class="element-featured-icon is--sm">
                <svg class="element-icon--eye-closed element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-eye-closed"></use></svg>
              </span>
            </span>
            <h2 v-html="highlightMatch(topic?.name)"></h2>
          </div>
          <div class="app-project-sources-golden-source-content__buttons">
            <button
              @click="toggleInfo()"
              class="element-chip--viewers element-chip is--info">
              Viewers <span :class="['element-chip__trailing', userCount > 1 ? 'is--error' : 'is--alt']">{{ userCount }}</span>
              <svg class="element-icon--chevron-down element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-down"></use></svg>
            </button> 
            <button class="element-button--primary element-button" @click="toggleEdit()">{{ isEditing ? 'Save' : 'Edit'  }}</button>
            <div class="app-project-sources-golden-source-content__buttons-status">
              <span class="app-project-sources-golden-source-content__buttons-status-item">
                <button class="element-chip is--success" @click="toggleReviewed()">
                  <svg class="element-icon--check-circle element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-check-circle"></use></svg>
                  {{ reviewText }}
                </button>
                <svg data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
                <div class="app-tooltip--screen-content-description app-tooltip dropdown-menu">
                  <h4 class="app-tooltip__title">Reviewed</h4>
                  <div class="app-tooltip__body">
                      Mark a topic as "Reviewed" once it's been reviewed for accuracy, including source references and AI-generated information.
                  </div>
                </div>
              </span>
              <span class="app-project-sources-golden-source-content__buttons-status-item">
                <button
                  @click="toggleFlagged()"
                  class="element-chip is--warning"
                  :disabled="isTopicToggleDisabled"
                  :title="topicToggleDisabledMessage">
                  <svg v-if="topic.flagged" class="element-icon--eye-closed element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-eye-closed"></use></svg>
                  <svg v-if="!topic.flagged" class="element-icon--eye-open element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-eye-open"></use></svg>
                  {{ flaggedTopic }}
                </button>
                <svg data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
                <div class="app-tooltip--screen-content-description app-tooltip dropdown-menu">
                  <h4 class="app-tooltip__title">Included/Excluded</h4>
                  <div class="app-tooltip__body">
                      Toggle to include or exclude this topic or reference as a source used for course content generation.
                  </div>
                </div>
              </span>
            </div>
            <div :class="['app-dropdown--viewers app-dropdown', { 'is--open': showInfo }]">
              <div class="app-dropdown__column">
                <h4 class="app-dropdown__title">Current Viewers</h4>
                <div class="app-dropdown__body">
                    <ul>
                        <li v-for="user in users"
                            :key="`user-${user.id}`"
                            class="user-list-item">
                            {{ user.name }}
                            <span v-if="user.id === currentUser.id">(You)</span>
                        </li>
                    </ul>
                </div>
              </div>
            </div>
          </div>
          <p class="app-project-sources-golden-source-content__timestamp">
            <span v-if="topic.reviewed">Reviewed by {{ topic.reviewed_by?.name }} on {{ topicUpdateAtFormattedTime }}</span>
            <span v-if="topic.reviewed && (topic.flagged || isTopicPartiallyFlagged)"> | </span>
            <span v-if="topic.flagged">Excluded by {{ topic.flagged_by?.name }} on {{ topicUpdateAtFormattedTime }}</span>
            <span v-if="isTopicPartiallyFlagged && !topic.flagged">At least one reference, but not all, are excluded</span>
          </p>
          <p v-if="isTopicToggleDisabled" class="app-project-sources-golden-source-content__timestamp">
            Topic cannot be included while all references are excluded. Include at least one reference.
          </p>
          <app-error class="is--inline"
            v-if="hasErrors"
            v-bind="{ errors }" />
      </div>
      <div class="app-project-sources-golden-source-content__content grid">
        <div class="app-project-sources-golden-source-content__item col-12">
          <h3 class="app-project-sources-golden-source-content__item-title">Summary
            <svg data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
            <div data-bs-toggle="dropdown" class="element-chip is--ai">
              <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
              AI
            </div>
            <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
              <h4 class="app-tooltip__title">AI Generated</h4>
              <div class="app-tooltip__body">
                <p>We’ve used AI to generate this content based on the information provided.</p>
              </div>
            </div>
              <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
                <h4 class="app-tooltip__title">Summary</h4>
                <div class="app-tooltip__body">
                  {{ summaryTooltip }}
                </div>
              </div>
          </h3>
          <p v-if="!isEditing" class="app-project-sources-golden-source-content__item-text" v-html="highlightMatch(changedJson.summary) || 'N/A'">
          </p> 
          <div v-else class="input textarea">
            <textarea v-model=changedJson.summary :placeholder="changedJson.summary"></textarea>
          </div>
        </div>
        <div class="app-project-sources-golden-source-content__item-group is--highlight col-12">
          <h3 class="app-project-sources-golden-source-content__item-group-title">
            Context
            <div data-bs-toggle="dropdown" class="element-chip is--ai">
              <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
              AI
            </div>
            <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
              <h4 class="app-tooltip__title">AI Generated</h4>
              <div class="app-tooltip__body">
                <p>We’ve used AI to generate this content based on the information provided.</p>
              </div>
            </div>
          </h3>
          <div v-if="topic?.metadata?.audience_suitability" class="app-project-sources-golden-source-content__item">
            <div class="app-project-sources-golden-source-content__item-title">Audience Level
              <svg data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
                <div class="app-tooltip--audience-level app-tooltip dropdown-menu">
                  <h4 class="app-tooltip__title">Audience Level</h4>
                  <div class="app-tooltip__body">
                    {{ audieneceTooltip }}
                  </div>
                </div>
            </div>
            <p v-if="!isEditing" class="app-project-sources-golden-source-content__item-text" v-html="highlightMatch(changedJson.audience_suitability) || 'N/A'">
            </p>
            <div v-else class="input textarea">
              <textarea v-model="changedJson.audience_suitability" :placeholder="changedJson.audience_suitability">
              </textarea>
            </div>
          </div>
          <div v-if="topic?.metadata?.prerequisites" class="app-project-sources-golden-source-content__item">
            <div class="app-project-sources-golden-source-content__item-title">Prerequisites
              <svg data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
                <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
                  <h4 class="app-tooltip__title">Prerequisites</h4>
                  <div class="app-tooltip__body">
                    {{ prerequisitesTooltip }}
                  </div>
                </div>
            </div>
            <p v-if="!isEditing" class="app-project-sources-golden-source-content__item-text" v-html="highlightMatch(changedJson.prerequisites) || 'N/A'">
            </p>
            <div v-else class="input textarea">
              <textarea v-model="changedJson.prerequisites" :placeholder="changedJson.prerequisites">
              </textarea>
            </div>
          </div>
          <div v-if="topic?.metadata?.related_topics" class="app-project-sources-golden-source-content__item">
            <div class="app-project-sources-golden-source-content__item-title">Related
              <svg data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
                <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
                  <h4 class="app-tooltip__title">Related</h4>
                  <div class="app-tooltip__body">
                    {{ related_topicsTooltip }}
                  </div>
                </div>
            </div>
            <p v-if="!isEditing" class="app-project-sources-golden-source-content__item-text" v-html="highlightMatch(changedJson.related_topics) || 'N/A'">
            </p>
            <div v-else class="input textarea">
              <textarea v-model="changedJson.related_topics" :placeholder="changedJson.related_topics">
              </textarea>
            </div>
          </div>
          <div v-if="topic?.metadata?.example_use_cases" class="app-project-sources-golden-source-content__item">
            <div class="app-project-sources-golden-source-content__item-title">Example Use Cases
              <svg data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
                <div class="app-tooltip--audience-level app-tooltip dropdown-menu">
                  <h4 class="app-tooltip__title">Example Use Cases</h4>
                  <div class="app-tooltip__body">
                    {{ exampleUseCasesTooltip }}
                  </div>
                </div>
            </div>
            <p v-if="!isEditing" class="app-project-sources-golden-source-content__item-text" v-html="highlightMatch(changedJson.example_use_cases) || 'N/A'">
            </p>
            <div v-else class="input textarea">
              <textarea v-model="changedJson.example_use_cases" :placeholder="changedJson.example_use_cases">
              </textarea>
            </div>
          </div>
        </div>
        <div class="app-project-sources-golden-source-content__item col-12">
          <h3 class="app-project-sources-golden-source-content__item-title">Key Takeaways for AI
            <svg data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
            <div data-bs-toggle="dropdown" class="element-chip is--ai">
              <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
              AI
            </div>
            <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
              <h4 class="app-tooltip__title">AI Generated</h4>
              <div class="app-tooltip__body">
                <p>We’ve used AI to generate this content based on the information provided.</p>
              </div>
            </div>
              <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
                <h4 class="app-tooltip__title">Key Takeaways for AI</h4>
                <div class="app-tooltip__body">
                  {{ keyAITakeawaysTooltip }}
                </div>
              </div>
          </h3>
          <p v-if="!isEditing" class="app-project-sources-golden-source-content__item-text" v-html="highlightMatch(changedJson.learning_objectives) || 'N/A'">
          </p>
          <div v-else class="input textarea">
            <textarea v-model="changedJson.learning_objectives" :placeholder="changedJson.learning_objectives"></textarea>
          </div>
        </div>
        <div class="app-project-sources-golden-source-content__item col-12">
          <h3 class="app-project-sources-golden-source-content__item-title">Notes for Internal Use
            <svg data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
              <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
                <h4 class="app-tooltip__title">Notes for Internal Use</h4>
                <div class="app-tooltip__body">
                  {{ internalNotesTooltip }}
                </div>
              </div>
          </h3>
          <div class="input textarea">
            <textarea v-model="changedJson.comment" :disabled="!isEditing" :placeholder="changedJson.comment ||'Take notes to refer back to here.'"></textarea>
          </div>
        </div>
        <div class="app-project-sources-golden-source-content__item col-12">
          <div class="app-project-sources-golden-source-content__item-title">
            References
            <svg data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
            <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
              <h3 class="app-tooltip__title">References</h3>
              <div class="app-tooltip__body">
                {{ referencesTooltip }}
              </div>
            </div>
          </div>
          <div v-for="(chunks, index) in chunkData" :key="chunks.id" :class="['app-project-sources-golden-source-content__reference', chunks.flagged ? 'is--excluded' : '']">
            <div v-if="!hasMedia(chunks)" class="app-project-sources-golden-source-content__reference-text">
              <p v-html="highlightMatch(chunks.content)"></p>
              <span class="element-chip is--citation" @click="showSourcesDrawer = true">{{ chunks.chunkIndex }}</span>
            </div>
            <div v-if="hasMedia(chunks)" class="app-project-sources-golden-source-content__reference-image grid">
              <div class="app-project-sources-golden-source-content__reference-image-img col-12 col-sm-4 col-lg-3">
                <img :src="chunks.asset_path">
                <span class="element-chip is--citation" @click="showSourcesDrawer = true">{{ chunks.imgIndex }}</span>
              </div>
              <div class="app-project-sources-golden-source-content__reference-image-content col-12 col-sm-8 col-lg-9">
                <div class="app-project-sources-golden-source-content__reference-image-title">Image Description
                  <div data-bs-toggle="dropdown" class="element-chip is--ai">
                    <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
                    AI
                  </div>
                  <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
                    <h4 class="app-tooltip__title">AI Generated</h4>
                    <div class="app-tooltip__body">
                      <p>We’ve used AI to generate this content based on the information provided.</p>
                    </div>
                  </div>
                </div>
                <p v-if="!isEditing" class="app-project-sources-golden-source-content__reference-image-text" v-html="highlightMatch(chunks.content)"></p>
                <div v-else class="input textarea">
                  <textarea v-model="chunks.content"></textarea>
                </div>
              </div>
            </div>
            <!--
            <button class="element-chip is--warning reference-toggle-btn" @click="handleReferenceFlagToggle(chunks)">
              <svg v-if="chunks.flagged" class="element-icon--eye-closed element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-eye-closed"></use></svg>
              <svg v-if="!chunks.flagged" class="element-icon--eye-open element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-eye-open"></use></svg>
              {{ referenceToggleText(chunks) }}
            </button>
            -->
          </div>
        </div>
        <transition name="slide-up">
          <div class="app-project-sources-golden-source-content__footer col-12 app-drawer__wrapper">
            <button @click="toggleSourcesDrawer()" class="app-project-sources-golden-source-content__footer-button app-drawer__wrapper-button">
              <svg class="element-icon--chevron-down element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-down"></use></svg>
              Citations
            </button>
            <div v-if="showSourcesDrawer" class="app-drawer">
              <div class="app-drawer__header">
                <h3 class="app-drawer__title">Reference Citations</h3>
                <div class="app-drawer__documents">
                  <div v-for="source in uniqueMediaUrls" :key="source.id" :class="['app-drawer__document', getDocumentClasses(source)]">
                    <a :href="source.url" download rel="noopener" class="element-button--ghost element-button">{{ source.name }}</a>
                  </div>
                </div>
              </div>
              <div class="app-drawer__content">
                <div v-for="source in chunkData" :key="source.id" class="app-drawer__citation">
                  <p><span class="element-chip">{{ source.chunkIndex || source.imgIndex }}</span> <em>{{ source.media.name }}</em>{{ chunkIndexDetails(source.media, source) }}</p>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <div v-if="showReferenceFlagConfirm" class="app-modal-dialog" @click.self="closeReferenceFlagConfirm()">
          <div class="app-modal-dialog__content">
            <div class="app-modal-dialog__header">
              <h4 class="app-modal-dialog__title">Are you sure you want to exclude this reference?</h4>
            </div>
            <div class="app-modal-dialog__body grid">
              <p class="col-12">Excluding this reference will exclude it across your entire Golden Source, including any references in other topics and domains. Do you still want to exclude the reference?</p>
              <div class="col-12">
                <div class="input checkbox">
                  <label class="checkbox__label" for="exclude-reference-do-not-show-again">
                    <input
                      id="exclude-reference-do-not-show-again"
                      v-model="hideReferenceFlagConfirm"
                      type="checkbox"
                      class="checkbox__input"
                      name="excludeReferenceDoNotShowAgain"
                    />
                    <span>Do not show this message again.</span>
                  </label>
                </div>
              </div>
            </div>
            <div class="app-modal-dialog__footer">
                <button class="element-button--secondary element-button" @click="closeReferenceFlagConfirm()">Cancel</button>
                <button class="element-button--primary element-button" @click="confirmReferenceFlagToggle()">Confirm</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="app-project-sources-golden-source-content element-empty-instructions">
      <img class="app-project-sources-golden-source-content__preview-body-empty-img" src="/img/undraw_select_option_re_u4qn.svg" />
      <p>Select a topic from the table of contents to view.</p>
    </div>
</template>
<script>
import api from '../api/ai'
import moment from 'moment'
import Echo from '../helpers/echo'
import { mapState } from 'vuex'
import DOMPurify from 'dompurify'
import { escapeRegExp } from 'lodash'

export default {
    name: 'ProjectSourcesContent',
    props: {
      currentTopic: {
        type: Object,
        default: null
      },
      sourceChunks: {
        type: Array,
        default: () => []
      }
    },
    data() {
      return {
        topic: this.currentTopic,
        isEditing: false,
        showSourcesDrawer: false,
        searchQuery: '',
        currentJson: {},
        changedJson: {},
        chunkData: this.currentTopic?.chunks || [],
        showInfo: false,
        isDefaultData: true,
        internalNotesTooltip: 'Notes here can be viewed by other users, but are not used in AI content generation',
        exampleUseCasesTooltip: 'Practical scenarios to which information from this topic can be applied.',
        audieneceTooltip: 'The suitability of the topic for different learner groups, as applicable. For example, proficiency level (beginner, intermediate, etc.) or role.',
        prerequisitesTooltip: 'Prior topics necessary for understanding the current topic.',
        related_topicsTooltip: ' Related topics and their relationships.',
        summaryTooltip: 'A brief description of the topic\'s main points.',
        keyAITakeawaysTooltip: 'Primary insights or goals for the topic.',
        referencesTooltip: 'Citations from the original document, including the document ID, page number, and paragraph index where the topic is mentioned. If the topic is mentioned in more than one section of the document, all citations for the topic will be displayed.',
        indexedReference: 0,
        errors: null,
        errorMessage: null,
        showReferenceFlagConfirm: false,
        hideReferenceFlagConfirm: false,
        pendingReferenceChunk: null,
      }
    },
    beforeMount() {
      let refIndex= 1
      this.chunkData.forEach(chunk => {
        if (chunk.media_path) {
          chunk.imgIndex = refIndex;
          refIndex++;
        } else {
          chunk.chunkIndex = refIndex
          refIndex++;
        }
      })
      const url = new URL(window.location.href)
      const searchParams = new URLSearchParams(url.search)
      const search = searchParams.get('search')
      if (search) {
        this.searchQuery = this.sanitizeQuery(search)
      }
    },
    mounted() {
      const originalPushState = history.pushState.bind(history)
      const originalReplaceState = history.replaceState.bind(history)

      history.pushState = (...args) => {
          originalPushState(...args)
          window.dispatchEvent(new Event('urlchange'))
      }
      history.replaceState = (...args) => {
          originalReplaceState(...args)
          window.dispatchEvent(new Event('urlchange'))
      }

      window.addEventListener('popstate', this.onUrlChange)
      window.addEventListener('urlchange', this.onUrlChange)
      if (this.topic)
      {
        this.formatSourceData()
        this.setupEchoListener()
      }

      this.hideReferenceFlagConfirm = this.shouldSkipReferenceFlagConfirm()
    },
    methods: {
      getDocumentClasses(source) {
          return {
            'is--pdf': source.url && source.name && source.name.match(/\.pdf$/i),
            'is--docx': source.url && source.name && source.name.match(/\.docx?$/i),
            'is--xlsx': source.url && source.name && source.name.match(/\.xlsx?$/i),
            'is--pptx': source.url && source.name && source.name.match(/\.pptx?$/i),
            'is--mp3': source.url && source.name && source.name.match(/\.mp3?$/i),
            'is--mp4': source.url && source.name && source.name.match(/\.mp4?$/i),
            'is--other': source.url && source.name && !source.name.match(/\.(jpg|jpeg|png|gif|pdf|mp3|mp4|docx?|xlsx?|pptx?)$/i)
          }
        },
      setupEchoListener () {
        const uuid = this.currentTopic?.uuid
        this.currentTopic.id = uuid
        this.$store.commit('builder/setSourceType', 'sourceTopic')
        this.$store.commit('builder/setSource', this.currentTopic)
        if (uuid)
        {
        Echo.join(this.$store.getters['builder/presenceChannel'])
            .here((users) => {
                this.$store.commit('builder/setUsers', users)
            })
            .joining((user) => {
                this.$store.commit('builder/addUser', user)
            })
            .leaving((user) => {
                this.$store.commit('builder/removeUser', user)
            })
        }
      },
      chunkIndexDetails(sourceDocument, chunk) {
        const docType = sourceDocument?.file_name ? sourceDocument.file_name.split('.').pop().toLowerCase() : ''
        const allowedTypes = ['pdf', 'docx', 'pptx', 'txt']
        const includeParagraph = true
        let str = ''
        if (allowedTypes.includes(docType)) {
          str += ': page ' + chunk.page_number
          if (includeParagraph)
            str += ', paragraph ' + (chunk.paragraph_index+1)
        }
        return str
      },
      toggleInfo() {
        this.showInfo = !this.showInfo;
      },
      formatSourceData() {
        const meta = this.topic?.metadata || {}

        if (Array.isArray(meta.related_topics) && meta.related_topics.length > 1) {
          this.changedJson.related_topics = meta.related_topics
            .map(item => `${item.topic_name} (${item.relationship_description})`)
            .join(', ')
        } else if (Array.isArray(meta.related_topics) && meta.related_topics.length === 1) {
          this.changedJson.related_topics = meta.related_topics[0] || ''
        } else {
          this.changedJson.related_topics = ''
        }

        if (Array.isArray(meta.audience_suitability) && meta.audience_suitability.length > 1) {
          this.changedJson.audience_suitability = meta.audience_suitability.join(', ')
        } else if (Array.isArray(meta.audience_suitability) && meta.audience_suitability.length === 1) {
          this.changedJson.audience_suitability = meta.audience_suitability[0] || ''
        } else {
          this.changedJson.audience_suitability = ''
        }

        if (Array.isArray(meta.learning_objectives) && meta.learning_objectives.length > 1) {
          this.changedJson.learning_objectives = meta.learning_objectives.join(' ')
        } else if (Array.isArray(meta.learning_objectives) && meta.learning_objectives.length === 1) {
          this.changedJson.learning_objectives = meta.learning_objectives[0] || ''
        } else {
          this.changedJson.learning_objectives = ''
        }

        if (Array.isArray(meta.prerequisites) && meta.prerequisites.length > 1) {
          this.changedJson.prerequisites = meta.prerequisites.join(', ')
        } else if (Array.isArray(meta.prerequisites) && meta.prerequisites.length === 1) {
          this.changedJson.prerequisites = meta.prerequisites[0] || ''
        } else {
          this.changedJson.prerequisites = ''
        }

        if (Array.isArray(meta.example_use_cases) && meta.example_use_cases.length > 1) {
          this.changedJson.example_use_cases = meta.example_use_cases.join(', ')
        } else if (Array.isArray(meta.example_use_cases) && meta.example_use_cases.length === 1) {
          this.changedJson.example_use_cases = meta.example_use_cases[0] || ''
        } else {
          this.changedJson.example_use_cases = ''
        }
        this.changedJson.summary = this.topic.metadata?.summary || ''
        this.changedJson.comment = this.topic?.comment || ''
        this.syncTopicData()
        this.currentJson.related_topics = this.topic.metadata?.related_topics[0] || ''
        this.currentJson.audience_suitability = this.topic.metadata?.audience_suitability[0] || ''
        this.currentJson.learning_objectives = this.topic.metadata?.learning_objectives[0] || ''
        this.currentJson.prerequisites = this.topic.metadata?.prerequisites[0] || ''
        this.currentJson.example_use_cases = this.topic.metadata?.example_use_cases[0] || ''
        this.currentJson.summary = this.topic.metadata?.summary || ''
        this.currentJson.comment = this.topic?.comment || ''
      },
      syncTopicData() {
          this.topic.metadata.related_topics = [this.changedJson.related_topics || ''];
          this.topic.metadata.audience_suitability = [this.changedJson.audience_suitability || ''];
          this.topic.metadata.learning_objectives = [this.changedJson.learning_objectives || ''];
          this.topic.metadata.prerequisites = [this.changedJson.prerequisites || ''];
          this.topic.metadata.example_use_cases = [this.changedJson.example_use_cases || ''];
          this.topic.metadata.summary = this.changedJson.summary || '';
          this.topic.comment = this.changedJson.comment || '';
          this.isDefaultData = false

      },
      async toggleEdit() {
        //do not reformat data just single array item as a string
        this.isEditing = !this.isEditing;
        if (!this.isEditing) {
          this.postToDB(); 
        }
      },
      async postToDB () {
          this.syncTopicData()

          const result = await api.updateTopicChunks(this.topic.project_source_id, this.topic.uuid, this.topic);

          if (result.errors) {
            // catch errors
            this.errors = result.errors
            this.errorMessage = result.message
            console.log(this.errors)
          } else {
            this.topic = result
            this.errors = this.errorMessage = null
            this.formatSourceData();
            console.log('Topic updated successfully:', result);
            this.$emit('topicUpdated', result);
          }
      },
      async postToChunksDB (chunk) {
          this.syncTopicData()

          const result = await api.updateChunk(this.topic.project_source_id, chunk.uuid, chunk)

          if (result.errors) {
            // catch errors
            this.errors = result.errors
            this.errorMessage = result.message
            console.log(this.errors)
          } else {
            chunk = result
            this.errors = this.errorMessage = null
            this.$emit('topicUpdated', this.topic)
          }
      },
      toggleReviewed() {
        this.topic.reviewed = !this.topic.reviewed;
        this.postToDB();
      },
      toggleFlagged() {
        this.topic.flagged = !this.topic.flagged;
        this.postToDB();
      },
      handleReferenceFlagToggle(chunk) {
        if (chunk.flagged || this.hideReferenceFlagConfirm) {
          this.toggleReferenceFlagged(chunk)
          return
        }

        this.pendingReferenceChunk = chunk
        this.showReferenceFlagConfirm = true
      },
      closeReferenceFlagConfirm() {
        this.showReferenceFlagConfirm = false
        this.pendingReferenceChunk = null
      },
      confirmReferenceFlagToggle() {
        if (!this.pendingReferenceChunk) {
          this.closeReferenceFlagConfirm()
          return
        }

        this.persistReferenceFlagConfirmPreference()
        this.toggleReferenceFlagged(this.pendingReferenceChunk)
        this.closeReferenceFlagConfirm()
      },
      flagPreferenceKey() {
        const userId = this.currentUser?.id;
        return `hideReferenceFlagConfirm:${this.topic?.project_source_id}:${userId}`
      },
      persistReferenceFlagConfirmPreference() {
        localStorage.setItem(this.flagPreferenceKey(), this.hideReferenceFlagConfirm ? '1' : '0')
      },
      shouldSkipReferenceFlagConfirm() {
        return localStorage.getItem(this.flagPreferenceKey()) === '1'
      },
      referenceToggleText(chunk) {
        return chunk?.flagged ? 'Excluded' : 'Included'
      },
      toggleReferenceFlagged(chunk) {
        chunk.flagged = !chunk.flagged;
        // const isNowAllFlagged = this.isTopicVirtuallyFlagged

        // if (isNowAllFlagged && !this.topic.flagged) {
        //   this.topic.flagged = true
        //   this.postToDB()
        // }
        this.postToChunksDB(chunk)
      },
      hasMedia(chunk) {
        return chunk.asset_path !== null && chunk.asset_path !== ''
      },
      citationIndex() {
        this.indexedReference++;
        return this.indexedReference;
      },
      toggleSourcesDrawer() {
        this.showSourcesDrawer = !this.showSourcesDrawer
      },
      sanitizeQuery (value) {
        if (typeof value !== 'string') return ''
        // Strip any HTML tags and limit length
        return DOMPurify.sanitize(value, { 
          ALLOWED_TAGS: [],
          ALLOWED_ATTR: [],
          RETURN_TRUSTED_TYPE: false 
        }).trim().slice(0, 200)
      },
      highlightMatch (text) {
        if (!this.searchQuery || !text) return text
        if (typeof text !== 'string') return text
        const escaped = escapeRegExp(this.searchQuery)
        const regex = new RegExp(`(${escaped})`, 'gi')
        return text.replace(regex, '<span class="search-highlight">$1</span>')
      },
      onUrlChange() {
        const params = new URLSearchParams(window.location.search)
        const search = params.get('search')
        if (!search && this.searchQuery) {
          this.searchQuery = ''
        } else if (search && search !== this.searchQuery) {
          this.searchQuery = this.sanitizeQuery(search)
        }
      }
    },
    computed: {
      ...mapState('builder',{
        users: state => state.users,
      }),
      currentUser () {
          return this.$store.state.user
      },
      userCount () {
          return this.users.length
      },
      topicUpdateAtFormattedTime() {
        return this.topic?.updated_at ? moment(this.topic.updated_at).format('MMM D, YYYY h:mm A') : 'N/A';
      },
      reviewText() {
        return this.topic?.reviewed ? 'Reviewed' : 'Unreviewed';
      },
      flagStatusTitle() {
        const statuses = []
        if (this.topic?.flagged) statuses.push('Topic excluded')
        if (this.isTopicVirtuallyFlagged) statuses.push('All references are excluded')
        if (this.isTopicPartiallyFlagged) statuses.push('Include at least one reference to include this topic')
        return statuses.join(' | ')
      },
      flaggedTopic() {
        return this.topic?.flagged ? 'Topic Excluded' : 'Topic Included';
      },
      isTopicToggleDisabled() {
        return this.isTopicVirtuallyFlagged;
      },
      topicToggleDisabledMessage() {
        return this.isTopicToggleDisabled ? 'Include at least one reference to include this topic.' : '';
      },
      flaggedChunkCount() {
        return this.chunkData.filter(c => c.flagged).length;
      },
      totalChunkCount() {
        return this.chunkData.length;
      },
      isTopicVirtuallyFlagged() {
        return this.totalChunkCount > 0 && this.flaggedChunkCount === this.totalChunkCount;
      },
      isTopicPartiallyFlagged() {
        return this.flaggedChunkCount > 0 && this.flaggedChunkCount < this.totalChunkCount;
      },
      hasErrors () {
        return this.errorMessage || (this.errors?.message ?? this.errors?.errors?.message)
      },
      uniqueMediaUrls() {
        const seenNames = new Set();
        const unique = [];
        this.chunkData.forEach(chunk => {
          if (chunk.media?.url && chunk.media?.name && !seenNames.has(chunk.media.url)) {
            seenNames.add(chunk.media.url);
            unique.push({ url: chunk.media.url, name: chunk.media.name });
          }
        });
        return unique
      }
    },
    watch: {
      currentTopic: {
        handler(newTopic) {
          this.topic = newTopic
          this.chunkData = newTopic?.chunks || []
          this.hideReferenceFlagConfirm = this.shouldSkipReferenceFlagConfirm()
        },
        deep: false
      },
      changedJson: {
        handler(newVal) {
          const isDifferent = JSON.stringify(newVal) !== JSON.stringify(this.currentJson);
          if (isDifferent && !this.isDefaultData && this.isEditing) {
            this.$emit('topicChanged', this.topic.uuid);
          } else if(!isDifferent && this.isEditing) {
            this.$emit('topicUpdated', this.topic);
          }
        },
        deep: true
      },
    },
    beforeDestroy() {
      window.removeEventListener('popstate', this.onUrlChange)
      window.removeEventListener('urlchange', this.onUrlChange)
    },
}
</script>
